import {
  CellClassParams,
  ColDef,
  ExcelExportParams,
  ShouldRowBeSkippedParams,
} from '@ag-grid-community/core';
import { computed, inject, ModelSignal, Signal } from '@angular/core';

import { MainQuery } from '@shared/store/main/main.query';
import { AuxExcelStyleKeys, decimalDivide } from '@shared/utils';

import { PATIENT_INVOICEABLES_BUFFER_COL_ID } from '../constants/patient-invoiceables-buffer-col-id.const';
import { CostType } from '../enums/cost-type';
import { InvestigatorForecastGridRow } from '../types/investigator-forecast-grid-row';

export const getPatientInvoiceablesBufferColDef = (
  patientInvoiceablesPercentage: ModelSignal<number | undefined>
): ColDef => ({
  aggFunc: null,
  cellClass: (params: CellClassParams<InvestigatorForecastGridRow>) => {
    const isPatientInvoiceablesRow =
      params.node?.aggData?.costType === CostType.PATIENT_INVOICEABLES;

    const isTotalRow = params.node?.data?.costType === 'Total';

    if (isTotalRow) {
      return [AuxExcelStyleKeys.HIDDEN_CELL];
    }

    if (isPatientInvoiceablesRow) {
      return [AuxExcelStyleKeys.PERCENT_WITH_ZERO];
    }

    return [AuxExcelStyleKeys.PERCENT];
  },
  colId: PATIENT_INVOICEABLES_BUFFER_COL_ID,
  headerName: 'Buffer',
  hide: true,
  valueGetter: (params) => {
    const isPatientInvoiceablesRow =
      params.node?.aggData?.costType === CostType.PATIENT_INVOICEABLES;

    if (isPatientInvoiceablesRow) {
      return decimalDivide(patientInvoiceablesPercentage() || 0, 100);
    }

    return 0;
  },
});

export const getExportTotalClasses = () => {
  const nonNumberColumns: (
    | keyof InvestigatorForecastGridRow
    | typeof PATIENT_INVOICEABLES_BUFFER_COL_ID
  )[] = ['costType', 'patientGroup', PATIENT_INVOICEABLES_BUFFER_COL_ID];

  return {
    [AuxExcelStyleKeys.TOTAL_ROW]: (params: CellClassParams<InvestigatorForecastGridRow>) =>
      params.data?.costType === 'Total' &&
      !nonNumberColumns.includes(params.column.getColId() as keyof InvestigatorForecastGridRow),
    [AuxExcelStyleKeys.TOTAL_ROW_HEADER]: (params: CellClassParams<InvestigatorForecastGridRow>) =>
      params.data?.costType === 'Total' &&
      nonNumberColumns.includes(params.column.getColId() as keyof InvestigatorForecastGridRow),
  };
};

export const INVESTIGATOR_FORECAST_EXCEL_CONFIG: ExcelExportParams = {
  fileName: 'Investigator_Forecast_Enrolled_Patients_Costs.xlsx',
  sheetName: 'Enrolled Patients Costs',
  shouldRowBeSkipped: (params: ShouldRowBeSkippedParams<InvestigatorForecastGridRow>) => {
    const isTotalRow = params.node?.data?.costType === 'Total';
    const isAggregated = !!params.node.aggData;
    const isPatientVisitsAggregatedRow =
      params.node?.aggData?.costType === CostType.PATIENT_VISITS && params.node.level === 0;

    return !isTotalRow && (isPatientVisitsAggregatedRow || !isAggregated);
  },
};

export const injectExcelParams = (columnDefsCount: Signal<number>): Signal<ExcelExportParams> => {
  const mainQuery = inject(MainQuery);

  return computed(() => ({
    ...INVESTIGATOR_FORECAST_EXCEL_CONFIG,
    prependContent: [
      {
        cells: [
          {
            data: { type: 'String', value: `Trial: ${mainQuery.selectedTrial()?.short_name}` },
            mergeAcross: columnDefsCount(),
            styleId: AuxExcelStyleKeys.FIRST_ROW,
          },
        ],
      },
    ],
  }));
};
